import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { IndexOrderTextData } from '../../shared/dto/EntityRepository';
import { useTextIndexing } from '../../shared/hooks/useTextIndexing';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { getBtnName } from '../../shared/constants/btnNames';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import IndexTextInfo, { TEXT_INDEX_DRAG_MIME_TYPE } from './IndexTextInfo';
import { getPageMessage } from '../../shared/constants/pageMessages';
import type { ReorderReq, UpdateReorderListReq } from '../../shared/dto/ReqDtoRepository';
import { useTextListActions } from '../../shared/hooks/useApi/useTextList';

const AUTO_SCROLL_SPEED = 7;

const IndexTextViewer: React.FC = () => {
  const { setIsIndexingMode, selectedIndexingTextList, mergeIndexingDropAtSlot, isChanged } =
    useTextIndexing();
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { reorderTexts, getManualOrderTextListForOwner } = useTextListActions();

  const [reorderPickIds, setReorderPickIds] = useState<string[]>([]);
  const [draggingBlockUiIds, setDraggingBlockUiIds] = useState<string[]>([]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragMovingIdsRef = useRef<string[]>([]);

  const contentContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const valid = new Set(selectedIndexingTextList.map((t) => String(t.id)));
    setReorderPickIds((prev) => prev.filter((id) => valid.has(id)));
  }, [selectedIndexingTextList]);

  useEffect(() => {
    void getManualOrderTextListForOwner();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도적으로 마운트 1회만 요청
  }, []);

  const toggleReorderPick = useCallback((id: string) => {
    const k = String(id);
    setReorderPickIds((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  }, []);

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current !== null) {
      cancelAnimationFrame(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const startAutoScroll = (direction: 'up' | 'down') => {
    stopAutoScroll();

    const scroll = () => {
      if (!contentContainerRef.current) return;

      const scrollAmount = direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED;
      contentContainerRef.current.scrollTop += scrollAmount;

      autoScrollIntervalRef.current = requestAnimationFrame(scroll);
    };

    autoScrollIntervalRef.current = requestAnimationFrame(scroll);
  };

  const handleContentDragOver = (e: React.DragEvent) => {
    if (dragMovingIdsRef.current.length === 0 || !contentContainerRef.current) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const container = contentContainerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseY = e.clientY;
    const scrollThreshold = 50;

    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      startAutoScroll('up');
    } else if (
      distanceFromBottom < scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight
    ) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  };

  const handleContentDragLeave = () => {
    stopAutoScroll();
  };

  useEffect(() => {
    if (dragMovingIdsRef.current.length === 0 && draggingBlockUiIds.length === 0) {
      stopAutoScroll();
    }

    return () => {
      stopAutoScroll();
    };
  }, [draggingBlockUiIds]);

  const resetAfterDrop = (movedIds: string[]) => {
    dragMovingIdsRef.current = [];
    setDraggingBlockUiIds([]);
    setDragOverIndex(null);
    if (movedIds.length > 1) {
      setReorderPickIds([]);
    }
  };

  const handleDragRowEnd = () => {
    setDraggingBlockUiIds([]);
    setDragOverIndex(null);
  };

  const beginReorderDrag = useCallback(
    (text: IndexOrderTextData) => {
      const lead = String(text.id);
      const orderedSel = selectedIndexingTextList
        .filter((t) => reorderPickIds.includes(String(t.id)))
        .map((t) => String(t.id));
      const block =
        orderedSel.length > 1 && reorderPickIds.includes(lead) ? orderedSel : [lead];
      dragMovingIdsRef.current = block;
      setDraggingBlockUiIds(block);
    },
    [selectedIndexingTextList, reorderPickIds]
  );

  const handleDropSpaceDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDropSpaceDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      const fromTransfer = e.dataTransfer.getData(TEXT_INDEX_DRAG_MIME_TYPE);
      const ids =
        dragMovingIdsRef.current.length > 0
          ? [...dragMovingIdsRef.current]
          : fromTransfer
            ? [String(fromTransfer)]
            : [];

      if (ids.length === 0) return;
      mergeIndexingDropAtSlot(ids, dropIndex);
      resetAfterDrop(ids);
    },
    [mergeIndexingDropAtSlot]
  );

  const handleSubmit = async () => {
    if (!isChanged) return;
    const reorderReqList: ReorderReq[] = selectedIndexingTextList.map((row, i) => ({
      id: String(row.id),
      index: i,
    }));
    await reorderTexts({
      id: '',
      reorderReqList: reorderReqList,
    } as UpdateReorderListReq);
  };

  const exitIndexingPanel = () => setIsIndexingMode(false);

  const handleCancel = () => {
    runAfterUnsavedChangesCheck(exitIndexingPanel);
  };

  const n = selectedIndexingTextList.length;

  return (
    <IndexTextInfoContainer>
      {selectedIndexingTextList.length === 0 && (
        <>
          <TextIndexingDescription>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_SELECT_SIDEBAR')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_DRAG_REORDER')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_UNLISTED_KEEP_ORDER')}
            </EachDescriptionLine>
          </TextIndexingDescription>
          <EmptySelectedIndexText>
            {getPageMessage('TEXT_INDEXING_EMPTY_SELECT_TEXT_FOR_ORDERING')}
          </EmptySelectedIndexText>
        </>
      )}
      {n >= 1 && (
        <ContentContainer
          ref={contentContainerRef}
          onDragOver={handleContentDragOver}
          onDragLeave={handleContentDragLeave}
        >
          <TextIndexingDescription>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_SELECT_SIDEBAR')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_DRAG_REORDER')}
            </EachDescriptionLine>
            <EachDescriptionLine>
              {getPageMessage('TEXT_INDEXING_HINT_UNLISTED_KEEP_ORDER')}
            </EachDescriptionLine>
          </TextIndexingDescription>
          {selectedIndexingTextList.map((text, index) => (
            <React.Fragment key={text.id}>
              <DropSpace
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={(e) => handleDropSpaceDragOver(e, index)}
                onDragLeave={handleDropSpaceDragLeave}
                $isActive={dragOverIndex === index}
              />
              <IndexTextInfo
                text={text}
                isDragging={draggingBlockUiIds.some(
                  (id) => String(id) === String(text.id)
                )}
                isReorderPicked={reorderPickIds.includes(String(text.id))}
                onToggleReorderPick={() => toggleReorderPick(text.id)}
                onDragStart={() => beginReorderDrag(text)}
                onDragEnd={handleDragRowEnd}
              />
            </React.Fragment>
          ))}
          <DropSpace
            onDrop={(e) => handleDrop(e, n)}
            onDragOver={(e) => handleDropSpaceDragOver(e, n)}
            onDragLeave={handleDropSpaceDragLeave}
            $isActive={dragOverIndex === n}
          />
          <DropSpaceLast
            onDrop={(e) => handleDrop(e, n)}
            onDragOver={(e) => handleDropSpaceDragOver(e, n)}
            onDragLeave={handleDropSpaceDragLeave}
          />
        </ContentContainer>
      )}
      <BtnContainer>
        {isChanged && (
          <HeadlessBtn value={getBtnName('CHANGE')} handleClick={handleSubmit} StyledBtn={BlackFitBtn} />
        )}
        <HeadlessBtn value={getBtnName('CANCEL')} handleClick={handleCancel} StyledBtn={PaperBlurFitBtn} />
      </BtnContainer>
    </IndexTextInfoContainer>
  );
};

const IndexTextInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 4px 0 8px 0;

  gap: 2px;
`;

const EmptySelectedIndexText = styled.div`
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.Body_03_2};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 65vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TextIndexingDescription = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
`;

const EachDescriptionLine = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  white-space: pre-line;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 10px 0px;
`;

const DropSpace = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  height: ${({ $isActive }) => ($isActive ? '60px' : '8px')};
  min-height: ${({ $isActive }) => ($isActive ? '30px' : '8px')};
  margin: 2px 0;
  border-radius: 2px;
  transition: all 0.4s;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `1px dashed ${theme.colors.color_Gray_05}` : 'none'};
`;

const DropSpaceLast = styled.div`
  width: 100%;
  flex: 1;
  min-height: 20px;
`;

export default IndexTextViewer;
